/**
 * The PARTIAL-costing warning (founder 2026-07-11). When some products are
 * selling for real money with no cost inputs, we genuinely can't compute the
 * true contribution margin — so every CM-derived card renders THIS instead of
 * a number. No estimate, no "based on X% of revenue" coverage figure: a plain
 * amber "not set up yet" warning (never red — nothing is broken and nobody is
 * losing money) whose click-through opens the /costs chat prefilled with
 * exactly the missing products, where the founder answers in plain English.
 *
 * THIS IS THE UNIVERSAL "we need something → open a chat" PATTERN. Future
 * gaps (missing lead times, missing payment terms, …) should reuse this exact
 * shape: card shows a warning instead of a number → click lands in the chat
 * that fixes it, pre-scoped to the exact gap. Don't invent new wizards.
 */

import { Link } from "react-router-dom";
import type { CmGap } from "../lib/card-requirements.js";

const AMBER = "#d97706";

/** The ready-made chat ask the "Fix it →" click drops into the costs chat
 *  (same hand-off the /costs needs-attention strip uses; the chat knows the
 *  full catalog, so naming the biggest few is enough to scope it). */
export function cmGapAsk(gap: CmGap): string {
  const names = gap.topNames.join(", ");
  if (gap.missingProducts === 1) return `Let's set costs for ${gap.topNames[0]}.`;
  if (gap.missingProducts <= gap.topNames.length) {
    return `Let's set costs for the products that are missing them: ${names}.`;
  }
  return `Let's set costs for the ${gap.missingProducts} products that are missing them — starting with ${names}.`;
}

/** The founder's exact warning sentence, singular/plural aware:
 *  "We can't calculate your true contribution margin yet — N products are
 *  missing costs (Name, Name, Name…)." */
export function cmGapBody(gap: CmGap): string {
  const n = gap.missingProducts;
  const extra = n - gap.topNames.length;
  const names = `${gap.topNames.join(", ")}${extra > 0 ? `, and ${extra} more` : ""}`;
  return `We can't calculate your true contribution margin yet — ${
    n === 1 ? "1 product is" : `${n} products are`
  } missing costs (${names}).`;
}

/** The "Fix it →" link every variant shares: /costs with the chat ask in
 *  router state (CostsPage consumes it, prefills the chat, scrolls to it).
 *  stopPropagation so a link inside a clickable card doesn't also drill. */
function FixItLink({ gap, fontSize = 12.5 }: { gap: CmGap; fontSize?: number }): React.ReactElement {
  return (
    <Link
      to="/costs"
      state={{ costsAsk: cmGapAsk(gap) }}
      onClick={(e) => e.stopPropagation()}
      style={{ color: AMBER, fontWeight: 700, fontSize, textDecoration: "none", whiteSpace: "nowrap" }}
    >
      Fix it →
    </Link>
  );
}

/** Full warning block — for wide cards (waterfall strip, heroes, chart cards):
 *  headline + the founder's full sentence + Fix it. */
export function CmGapNotice({ gap }: { gap: CmGap }): React.ReactElement {
  return (
    <div
      role="status"
      style={{
        borderRadius: 10,
        border: `1px solid color-mix(in srgb, ${AMBER} 45%, var(--border))`,
        background: `color-mix(in srgb, ${AMBER} 8%, transparent)`,
        padding: "12px 14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, color: AMBER, fontWeight: 800, fontSize: 13 }}>
        <span aria-hidden="true">⚠️</span> Can't calculate yet
      </div>
      <div style={{ marginTop: 5, fontSize: 12.5, color: "var(--body)", lineHeight: 1.55 }}>
        {cmGapBody(gap)} <FixItLink gap={gap} />
      </div>
    </div>
  );
}

/** Compact warning — for the small lever/hero tiles where the full sentence
 *  doesn't fit: "Can't calculate yet" where the number was, the count, Fix it. */
export function CmGapCompact({ gap }: { gap: CmGap }): React.ReactElement {
  const n = gap.missingProducts;
  return (
    <div>
      <div style={{ color: AMBER, fontSize: 14.5, fontWeight: 800, margin: "5px 0 2px" }}>
        <span aria-hidden="true">⚠️</span> Can't calculate yet
      </div>
      <div style={{ fontSize: 10.5, color: "var(--body)", lineHeight: 1.45 }}>
        {n} product{n === 1 ? " is" : "s are"} missing costs · <FixItLink gap={gap} fontSize={10.5} />
      </div>
    </div>
  );
}

